import { FartherManagedAccount } from "@fartherfinance/frontend/api/Accounts/Types";
import {
  getExternalAccountBalance,
  getPartialAccountNumber,
  MASK_NUMBER,
} from "@fartherfinance/frontend/api/Accounts/utilities/accountUtil";
import {
  ExternalAccountExtended,
  PlaidAccountExtended,
} from "@fartherfinance/frontend/api/ExternalAccount/hooks/useExternalAccountsExtended";
import {
  ExternalAccount,
  ManualAccount,
} from "@fartherfinance/frontend/api/ExternalAccount/requests/getExternalAccounts";
import { BalanceInfo } from "@fartherfinance/frontend/api/PerformanceGroups/hooks/useAllAccountBalances";
import { AccountType } from "@fartherfinance/frontend/api/Types";

import removeCommas from "@src/utils/CommaHelperFunctions/removeCommas";
import toUSD from "@src/utils/toUSD";

import {
  CreateTransferDepositFormData,
  CreateTransferFormData,
  CreateTransferInvestmentFormData,
  CreateTransferIRAFormData,
  CreateTransferWithdrawalFormData,
} from "./const";

export const createAccountLabel = (
  account: FartherManagedAccount,
  // number means known balance, null means unknown balance, undefined is loading balance
  balance: number | null | undefined,
  isAdvisor: boolean
): string => {
  const custodian = account.accountDetails.custodian;
  const custodianAccountNumber =
    account.accountDetails.custodianAccountNumber ?? "";

  const accountName =
    account.accountDetails.displayName === "Long-term Savings"
      ? "Long-term Growth"
      : account.accountDetails.displayName;

  const balanceStr =
    balance === null
      ? "--"
      : balance === undefined
      ? "(Loading balance)"
      : toUSD(balance);

  if (isAdvisor) {
    return `${accountName} - ${custodian} ${custodianAccountNumber} - ${balanceStr}`;
  }

  return `${accountName} - ${balanceStr}`;
};

export const createFundingAccountLabel = (
  fundingAccount: ExternalAccountExtended | null | undefined
): string => {
  const fundingAccountName = fundingAccount
    ? isManualAccount(fundingAccount)
      ? fundingAccount.details.account.accountTitle
      : fundingAccount.details.account.name
    : undefined;

  const partialAccountNumber = `(...${
    fundingAccount ? getPartialAccountNumber(fundingAccount) : MASK_NUMBER
  })`;

  const fundingAccountBalance =
    fundingAccount && isPlaidAccount(fundingAccount)
      ? getExternalAccountBalance(fundingAccount)
      : null;

  const formattedFundingAccountBalance = fundingAccountBalance
    ? toUSD(fundingAccountBalance)
    : "";

  return fundingAccountName
    ? `${fundingAccountName} ${partialAccountNumber} ${formattedFundingAccountBalance}`
    : "--";
};

const isManualAccount = (
  acc: ExternalAccountExtended | ExternalAccount
): acc is ManualAccount => {
  return acc.accountProvider === "manual";
};

export const isPlaidAccount = (
  acc: ExternalAccountExtended
): acc is PlaidAccountExtended => {
  return acc.accountProvider === "plaid" && "link" in acc;
};

export const isAccountTypeIRA = (accountType: AccountType): boolean => {
  return (
    accountType === "IRA" ||
    accountType === "Roth IRA" ||
    accountType === "Sep IRA" ||
    accountType === "Rollover IRA"
  );
};

export const isDepositForm = (
  formData: CreateTransferFormData
): formData is CreateTransferDepositFormData =>
  formData.transferType.value === "Deposit";

export const isWithdrawalForm = (
  formData: CreateTransferFormData
): formData is CreateTransferWithdrawalFormData =>
  formData.transferType.value === "Withdrawal";

export const isInvestmentForm = (
  formData: CreateTransferFormData
): formData is CreateTransferInvestmentFormData =>
  formData.transferType.value === "Investment";

export const isIRAForm = (
  formData: CreateTransferFormData
): formData is CreateTransferIRAFormData =>
  formData.transferType.value === "IRA";

export const findAccountBalance = (
  virtualAccountId: string,
  allAccountBalances: BalanceInfo[]
): number | null => {
  return (
    allAccountBalances.find((balance) => balance.accountId === virtualAccountId)
      ?.balance ?? null
  );
};

export const prepareTransferSummary = (
  formData: CreateTransferFormData,
  allAccountBalances: BalanceInfo[],
  isAdvisor: boolean
) => {
  if (isDepositForm(formData)) {
    const {
      depositAccount,
      depositAmount,
      depositContributionYear,
      withdrawalAccountLabel,
      transferType,
    } = formData;

    const depositAccountBalance = findAccountBalance(
      depositAccount.account.virtualAccountId,
      allAccountBalances
    );

    return {
      amount: toUSD(Number(removeCommas(depositAmount))),
      transferType: transferType.value,
      withdrawFrom: withdrawalAccountLabel,
      depositTo: createAccountLabel(
        depositAccount.account,
        depositAccountBalance,
        isAdvisor
      ),
      contributionYear: depositContributionYear?.label,
    };
  }

  if (isWithdrawalForm(formData)) {
    const {
      withdrawalAccount,
      withdrawalAmount,
      depositAccountLabel,
      transferType,
    } = formData;

    const withdrawalAccountBalance = findAccountBalance(
      withdrawalAccount.account.virtualAccountId,
      allAccountBalances
    );

    return {
      amount: toUSD(Number(removeCommas(withdrawalAmount))),
      transferType: transferType.value,
      withdrawFrom: createAccountLabel(
        withdrawalAccount.account,
        withdrawalAccountBalance,
        isAdvisor
      ),
      depositTo: depositAccountLabel,
    };
  }

  if (isInvestmentForm(formData)) {
    const {
      transferType,
      institution,
      accountType,
      accountNumber,
      accountHolderName,
      accountDestination,
      requestSigned,
    } = formData;

    return {
      amount: null,
      transferType: transferType.value,
      accountType: accountType.label,
      institution: institution.label,
      accountNumber: accountNumber,
      accountHolderName: accountHolderName,
      accountDestination: accountDestination.label,
      requestSigned: requestSigned,
    };
  }

  if (isIRAForm(formData)) {
    const {
      transferType,
      institution,
      iraType,
      accountNumber,
      accountHolderName,
      accountDestination,
      requestSigned,
    } = formData;

    return {
      amount: null,
      transferType: transferType.value,
      iraType: iraType.label,
      institution: institution.label,
      accountNumber: accountNumber,
      accountHolderName: accountHolderName,
      accountDestination: accountDestination.label,
      requestSigned: requestSigned,
    };
  }

  return {
    amount: null,
    transferType: "",
  };
};
